import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import UpdatePrompt from "@/components/UpdatePrompt";
import PositionFilledListener from "@/components/PositionFilledListener";
import { initPWA } from "@/lib/pwa";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import EmployerDashboard from "@/pages/EmployerDashboard";
import TOCDashboard from "@/pages/TOCDashboard";
import SchoolSettings from "@/pages/SchoolSettings";
import CoAdminManagement from "@/pages/CoAdminManagement";
import AcceptCoAdminInvite from "@/pages/AcceptCoAdminInvite";
import Analytics from "@/pages/Analytics";
import TOCManagement from "@/pages/TOCManagement";
import Subscription from "@/pages/Subscription";
import SubscriptionAccount from "@/pages/SubscriptionAccount";
import AdminGuide from "@/pages/guides/AdminGuide";
import SubGuide from "@/pages/guides/SubGuide";
import AdminOnboarding from "@/pages/guides/AdminOnboarding";
import SubOnboarding from "@/pages/guides/SubOnboarding";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import NotificationSettings from "@/pages/NotificationSettings";
import ProfilePage from "@/pages/ProfilePage";
import AdminDashboard from "@/pages/AdminDashboard";
import Pricing from "@/pages/Pricing";
import BillingPlans from "@/pages/BillingPlans";
import ContactUs from "@/pages/ContactUs";
import ProtectedRoute from "@/components/ProtectedRoute";
import { PlanLimitsProvider } from "@/context/PlanLimitsContext";
import "@/App.css";

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/accept-invite" element={<AcceptCoAdminInvite />} />
      <Route path="/profile" element={<ProfilePage />} />
      
      {/* Guide Routes (Public) */}
      <Route path="/guides/admin" element={<AdminGuide />} />
      <Route path="/guides/sub" element={<SubGuide />} />
      <Route path="/onboarding/admin" element={<AdminOnboarding />} />
      <Route path="/onboarding/sub" element={<SubOnboarding />} />
      
      {/* Admin (Subscription Manager) Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="subscription_manager">
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Employer Routes */}
      {/* Plan-limits-gated employer routes share one PlanLimitsProvider instance so
          GET /subscription/plans and /status are fetched once per session, not once
          per page navigated to within this group (plan-limits-service spec). */}
      <Route element={<PlanLimitsProvider><Outlet /></PlanLimitsProvider>}>
        <Route path="/employer" element={
          <ProtectedRoute requiredRole="employer">
            <EmployerDashboard />
          </ProtectedRoute>
        } />
        <Route path="/employer/school/:schoolId" element={
          <ProtectedRoute requiredRole="employer">
            <SchoolSettings />
          </ProtectedRoute>
        } />
        <Route path="/employer/school/:schoolId/tocs" element={
          <ProtectedRoute requiredRole="employer">
            <TOCManagement />
          </ProtectedRoute>
        } />
        <Route path="/employer/analytics/:schoolId" element={
          <ProtectedRoute requiredRole="employer">
            <Analytics />
          </ProtectedRoute>
        } />
      </Route>
      {/* Account-level subscription management — works with zero schools (the
          subscription belongs to the school-admin account, not a school). */}
      <Route path="/employer/subscription" element={
        <ProtectedRoute requiredRole="employer">
          <SubscriptionAccount />
        </ProtectedRoute>
      } />
      {/* Own provider instance: BillingPlans shows usage-vs-limits per plan (PlanUsageComparison). */}
      <Route element={<PlanLimitsProvider><Outlet /></PlanLimitsProvider>}>
        <Route path="/employer/billing" element={
          <ProtectedRoute requiredRole="employer">
            <BillingPlans />
          </ProtectedRoute>
        } />
      </Route>
      <Route path="/employer/school/:schoolId/co-admins" element={
        <ProtectedRoute requiredRole="employer">
          <CoAdminManagement />
        </ProtectedRoute>
      } />
      <Route path="/employer/school/:schoolId/subscription" element={
        <ProtectedRoute requiredRole="employer">
          <Subscription />
        </ProtectedRoute>
      } />

      {/* TOC Routes */}
      <Route element={<PlanLimitsProvider><Outlet /></PlanLimitsProvider>}>
        <Route path="/toc" element={
          <ProtectedRoute requiredRole="toc">
            <TOCDashboard />
          </ProtectedRoute>
        } />
        <Route path="/toc/analytics" element={
          <ProtectedRoute requiredRole="toc">
            <Analytics />
          </ProtectedRoute>
        } />
      </Route>
      <Route path="/toc/settings/notifications" element={
        <ProtectedRoute requiredRole="toc">
          <NotificationSettings />
        </ProtectedRoute>
      } />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/contact" element={<ContactUs />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Initialize PWA on app load
  useEffect(() => {
    initPWA();
  }, []);

  return (
    <div className="App min-h-screen bg-slate-50">
      <BrowserRouter>
        <AppRouter />
        <PWAInstallBanner />
        <UpdatePrompt />
        {/* KAN-156: real-time position-filled chime + toast for school admins */}
        <PositionFilledListener />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
